import {
  Button,
  ButtonGroup,
  Icon,
  Intent,
  Menu,
  MenuItem,
  Popover,
  DialogBody,
  DialogFooter,
  Tooltip,
  Dialog
} from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { Accordion, DropDown, SearchInput, SelectItem } from '@zaiusinc/hera';
import { useObserver } from 'mobx-react-lite';
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { LoadingIndicator } from '../../../components/LoadingIndicator';
import { showToasts } from '../../../lib/toaster';
import { DataSyncItem, SyncStrategy, OptimizelyProduct } from '../../../types/DataSyncItem';
import styles from './DataSyncList.module.sass';
import { DataSyncExecution, DataSyncExecutionStatus } from '../../../types/DataSyncExecution';
import DataSyncIcons from './DataSyncIcons';
import { SegmentAnalytics } from '../../../lib/SegmentAnalytics';
import { DataSyncStrategyProvider } from '../DataSyncStrategyProvider';
import { SortDirection } from '../../../types/Sort';
import { DataSyncStore } from '../../../stores/DataSyncStore';

const filterByOptions: SelectItem[] = [
  { text: 'All', value: 'all'},
  { text: 'Active', value: 'active'},
  { text: 'Paused', value: 'paused'},
];

const sortByOptions: SelectItem[] = [
  { text: 'Created Date', value: 'createdAt'},
  { text: 'Destination', value: 'destination.destinationObject.objectName'},
  { text: 'Destination Product', value: 'destination.productName'},
  { text: 'Last Sync', value: 'lastExecution.createdAt'},
  { text: 'Name', value: 'name'},
  { text: 'Source', value: 'source.sourceObject.objectName'},
  { text: 'Source Product', value: 'source.productName'},
  { text: 'Updated Date', value: 'updatedAt'},
];

const DataSyncList = ({dataSyncStore}: { dataSyncStore: DataSyncStore}) => {
  const history = useHistory();
  const [deleteModalData, setDeleteModalData] = useState<any>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [canTriggerSync, setCanTriggerSync] = useState<boolean>(false);
  const [isCheckingTriggerSync, setIsCheckingTriggerSync] = useState<boolean>(false);

  return useObserver(() => {
    const shouldShowPauseStartMenu = (data: DataSyncItem) => {
      return DataSyncStrategyProvider.getStrategy(data) === SyncStrategy.SCHEDULED &&
             data.source.product !== OptimizelyProduct.HUB_APP;
    };

    const renderEmptyState = () => (
      <div className={styles.emptyDataSyncList}>
        <Icon icon={IconNames.SEARCH} iconSize={64}/>
        <p>No data syncs were found</p>
      </div>
    );

    const getDisabledTooltipText = (data: DataSyncItem) => {
      if (isCheckingTriggerSync) {
        return 'Checking if sync can be triggered...';
      }
      if (!canTriggerSync) {
        if (data.source.product === OptimizelyProduct.HUB_APP) {
          return 'No job configuration found for this data sync';
        }
        return 'Sync cannot be triggered at this time';
      }
      return '';
    };

    const renderMenu = (data: DataSyncItem) => (
      <Menu className={styles.menu}>
        <MenuItem htmlTitle="Executions" text="Executions"
                  onClick={() => history.push(`/data_syncs/${data.id}/executions`)}/>
        <Tooltip
          content={getDisabledTooltipText(data)}
          disabled={canTriggerSync && !isCheckingTriggerSync}
          fill={true}
        >
          <MenuItem
            text="Trigger now"
            icon={isCheckingTriggerSync ? IconNames.REFRESH : undefined}
            disabled={!canTriggerSync || isCheckingTriggerSync}
            onClick={() => handleTriggerSync(data)}
          />
        </Tooltip>
        {shouldShowPauseStartMenu(data) &&
          <MenuItem
            htmlTitle="Pause/Start"
            text={data.enabled ? 'Pause' : 'Start'}
            onClick={() => handleToggleDataSync(data)}
          />
        }
        <MenuItem htmlTitle="Edit" text="Edit" onClick={() => history.push(`/data_syncs/${data.id}`)}/>
        <MenuItem htmlTitle="Delete" text="Delete" onClick={(event) => {
          setDeleteModalData({
            dataSync: data,
          });
        }
        }/>
      </Menu>
    );

    const handleToggleDataSync = async (dataSync: DataSyncItem) => {
      await SegmentAnalytics.track('Data Sync Action', {
        action: dataSync.enabled ? 'Pause' : 'Start',
        path: window.location.pathname + window.location.search,
        dataSyncId: dataSync.id
      });
      try {
        await dataSyncStore.toggleDataSync(dataSync.id);
      } catch (error) {
        showToasts([{intent: 'warning', message: `Cannot start/pause data sync. ${error}`}]);
      }
    };

    const handleDeleteDataSync = async (dataSync: DataSyncItem) => {
      if (isDeleting) return;
      setIsDeleting(true);
      await SegmentAnalytics.track('Data Sync Action', {
        action: 'Delete',
        path: window.location.pathname + window.location.search,
        dataSyncId: dataSync.id
      });

      try {
        await dataSyncStore.deleteDataSync(dataSync.id);
        showToasts([{intent: 'success', message: 'Data sync deleted successfully'}]);
      } catch (error) {
        showToasts([{intent: 'warning', message: `Cannot delete data sync. ${error}`}]);
      } finally {
        setIsDeleting(false);
      }
    };

    const handleTriggerSync = async (dataSync: DataSyncItem) => {
      await SegmentAnalytics.track('Data Sync Action', {
        action: 'Trigger',
        path: window.location.pathname + window.location.search,
        dataSyncId: dataSync.id
      });
      if (await dataSyncStore.triggerSync(dataSync)) {
        showToasts([{intent: 'success', message: 'Sync triggered successfully'}]);
      } else {
        showToasts([{intent: 'warning', message: 'Failed to trigger sync'}]);
      }
    };

    const renderDataSyncCardHeader = (data: DataSyncItem) => (
      <div key={`${data.id}-head`} className={styles.cardHeader}>
        <div className={styles.cardTitle}>
          <DataSyncIcons dataSync={data} />
          <span>{data.name}</span>
        </div>
        <div className={styles.cardLastStatus}>
          {/* Show always active status for source apps */}
          {data.source.product === OptimizelyProduct.HUB_APP && (
            <div className={styles.cardStatusMessage}>
              <Icon icon={IconNames.HISTORY} />
              <span style={{ width: '71px' }}>
                <b>Active</b>
              </span>
              <Icon icon={IconNames.FULL_CIRCLE} intent={Intent.SUCCESS} />
              <span>Real-time sync</span>
            </div>
          )}
          {data.source.product !== OptimizelyProduct.HUB_APP &&
            DataSyncStrategyProvider.getStrategy(data) ===
              SyncStrategy.SCHEDULED &&
            getLastSyncStatus(data)}
          <div onClick={(e) => e.stopPropagation()}>
            <Popover
              content={renderMenu(data)}
              placement="bottom"
              popoverClassName={styles.popoverMenu}
              onOpening={async () => {
                setIsCheckingTriggerSync(true);
                try {
                  const canTrigger = await dataSyncStore.canTriggerSync(data);
                  setCanTriggerSync(canTrigger);
                } finally {
                  setIsCheckingTriggerSync(false);
                }
              }}
            >
              <button className={styles.cardEllipsis}>...</button>
            </Popover>
          </div>
        </div>
      </div>
    );

    const getLastSyncStatus = (data: DataSyncItem) => {
      const lastExecution = data.lastExecution;
      const activeIcon = data.enabled ?
        <Icon icon={IconNames.HISTORY}/> :
        <Icon icon={IconNames.PAUSE}/>;

      const activeLabel = <span style={{width: '71px'}}>
        <b>{data.enabled ? 'Active' : 'Paused'}</b>
      </span>;

      if (!lastExecution) {
        return <div className={styles.cardStatusMessage}>
          {activeIcon}
          {activeLabel}
          <Icon icon={IconNames.FULL_CIRCLE} intent={Intent.WARNING}/>
          <span>It has never been executed</span>
        </div>;
      }

      let healthIcon: React.ReactNode;
      let healthLabel: string;
      const options: Intl.DateTimeFormatOptions = {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      };
      const formattedDate = new Date(lastExecution.created_at).toLocaleString('en-US', options);
      switch (lastExecution.status) {
        case DataSyncExecutionStatus.COMPLETED:
          healthIcon = <Icon icon={IconNames.FULL_CIRCLE} intent={Intent.SUCCESS}/>;
          healthLabel = `Last synced on ${formattedDate}`;
          break;
        case DataSyncExecutionStatus.FAILED:
          healthIcon = <Icon icon={IconNames.ERROR} intent={Intent.DANGER}/>;
          healthLabel = `Last synced on ${formattedDate}`;
          break;
        case DataSyncExecutionStatus.IN_PROGRESS:
          healthIcon = <Icon icon={IconNames.REFRESH} intent={Intent.PRIMARY}/>;
          healthLabel = 'Syncing...';
          break;
        default:
          healthIcon = <Icon icon={IconNames.FULL_CIRCLE} intent={Intent.NONE}/>;
          healthLabel = `Last synced on ${formattedDate}`;
          break;
      }
      return <div className={styles.cardStatusMessage}>
        {activeIcon}
        {activeLabel}
        {healthIcon}
        <span>{healthLabel}</span>
      </div>;
    };

    const renderItemsNumber = (execution?: DataSyncExecution) => {
      if (!execution) {
        return <p>No stats yet</p>;
      }

      if (execution.itemsImported === undefined && execution.itemsDropped === undefined) {
        return <p>No stats available</p>;
      }
      return (
        <>
          {execution.itemsImported !== undefined ?
            (<>Items Imported: <b>{execution.itemsImported}</b></>) : (<>Items</>)}
          {execution.itemsDropped !== undefined  && <> Dropped: <b>{execution.itemsDropped}</b></>}
        </>
      );
    };

    const renderDataSyncCard = (data: DataSyncItem) => (
      <Accordion
        key={data.id}
        header={renderDataSyncCardHeader(data)}
        startOpen={false}
        minimal={true}
        className={styles.card}
      >
        <div className={styles.cardDetails}>
          <p>
            {data.source.object.object_display_name || data.source.object.object_name}
            <Icon icon={IconNames.CARET_RIGHT}/>
            {data.destination.object.object_name}
          </p>
          {DataSyncStrategyProvider.getStrategy(data) === SyncStrategy.SCHEDULED &&
            <table className={styles.carDetailsTable}>
              <thead>
              <tr>
                <th>TOTAL SYNC</th>
                <th className={styles.carDetailsTableError}>LAST ERROR</th>
              </tr>
              </thead>
              <tbody>
              <tr>
                <td>{renderItemsNumber(data.lastExecution)}</td>
                <td>
                  {data.lastExecution?.status === 'FAILED' ?
                    <div className={styles.lastError}>{data.lastExecution?.message}</div> :
                    <div>None</div>}
                </td>
              </tr>
              </tbody>
          </table>}
        </div>
      </Accordion>
    );

    const renderDeleteDialog = () => {
      const isOpen = !!deleteModalData;
      if (!isOpen) {
        return null;
      }

      const dataSyncToDelete = deleteModalData.dataSync;
      const closeDialog = () => {
        if (!isDeleting) {
          setDeleteModalData(null);
        }
      };

      return <Dialog
        isOpen={isOpen}
        title="Delete Data Sync"
        onClose={closeDialog}
      >
        <DialogBody>
          <p>Are you sure you want to delete this data sync?</p>
        </DialogBody>
        <DialogFooter actions={
          <>
            <Button text="Cancel" onClick={closeDialog} disabled={isDeleting}/>
            <Button
              intent={Intent.DANGER}
              text={isDeleting ? 'Deleting...' : 'Delete'}
              loading={isDeleting}
              disabled={isDeleting}
              onClick={async () => {
                if (dataSyncToDelete && !isDeleting) {
                  await handleDeleteDataSync(dataSyncToDelete);
                  closeDialog();
                }
              }}
            />
          </>
        }/>
      </Dialog>;
    };

    if (dataSyncStore.loading) {
      return <LoadingIndicator/>;
    }

    return (
      <div className={styles.wrapper}>
        <div className={styles.listTop}>
          <SearchInput
            className={styles.searchInput}
            onChange={(value) => dataSyncStore.searchFilter = value.trim()}
            placeholder="Find Data Sync"
            searchTerm={dataSyncStore.searchFilter}
          />
          <div className={styles.filtersSection}>
            <span className={styles.filter}>Filter by</span>
            <DropDown
              items={filterByOptions}
              value={dataSyncStore.filterByStatus}
              onItemSelect={(status) => dataSyncStore.filterByStatus = status.value}
            />
            <span style={{width: '20px'}}></span>
            <span className={styles.filter}>Sort by</span>
            <ButtonGroup>
              <Button
                icon={dataSyncStore.sort.direction === SortDirection.DESC ? 'sort-desc' : 'sort-asc'}
                onClick={() => {
                  const direction = dataSyncStore.sort.direction === SortDirection.DESC
                    ? SortDirection.ASC
                    : SortDirection.DESC;
                  dataSyncStore.setSortDirection(direction);
                }}
              />
              <DropDown
                items={sortByOptions}
                initialContent="Updated Date"
                value={dataSyncStore.sort.field}
                onItemSelect={(status) => dataSyncStore.setSortField(status.value)}
              />
            </ButtonGroup>
          </div>
        </div>
        {renderDeleteDialog()}
        <div className={styles.listContent}>
          {dataSyncStore.dataSyncs.length === 0 ? renderEmptyState() : dataSyncStore.dataSyncs.map(renderDataSyncCard)}
        </div>
      </div>
    );
  });
};

export default DataSyncList;
